import { useEffect, useState } from "react";
import { api } from "./api";

const cache = {};
const load = (url, empty) => {
  if (!cache[url]) cache[url] = api.get(url).then((r) => r.data || empty).catch(() => { delete cache[url]; return empty; });
  return cache[url];
};

const useRemote = (url, empty) => {
  const [state, setState] = useState({ data: empty, ready: false });
  useEffect(() => {
    let on = true;
    load(url, empty).then((data) => on && setState({ data, ready: true }));
    return () => { on = false; };
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
};

const EMPTY_CATALOG = { doors: [], options: [] };
export const useDoorCatalog = () => {
  const { data, ready } = useRemote("/door-catalog", EMPTY_CATALOG);
  return { ...data, ready };
};
export const useProfileSystems = () => useRemote("/profile-systems", []).data;
export const refreshCatalog = () => Object.keys(cache).forEach((k) => delete cache[k]);

export const MATERIAL_OPTIONS = [{ id: "kunststof", label: "Kunststof" }, { id: "aluminium", label: "Aluminium" }];
export const GLASS_FILTER = [{ id: "met", label: "Met glas" }, { id: "zonder", label: "Zonder glas" }];
export const OPTION_GROUPS = [
  { key: "stijl", label: "Stijlen", hint: "Bijv. modern, klassiek, landelijk" },
  { key: "type", label: "Type deur", hint: "Bijv. voordeur, achterdeur, tuindeur" },
  { key: "kleur", label: "Kleuren", hint: "Met kleurstaal voor de filter" },
  { key: "glas", label: "Glasuitvoeringen", hint: "Keuze voor deuren met glas" },
];

// Filtergroepen van de deurengalerij: binnen een groep OF, tussen groepen EN.
export const FILTER_GROUPS = [
  { key: "material", label: "Materiaal", values: (d) => [d.material] },
  { key: "stijl", label: "Stijl", values: (d) => d.styles },
  { key: "type", label: "Type deur", values: (d) => d.types },
  { key: "glas", label: "Glas", values: (d) => [d.has_glass ? "met" : "zonder"] },
  { key: "kleur", label: "Kleur", values: (d) => d.colors },
];

export const matchesFilters = (door, filters, skip) =>
  FILTER_GROUPS.every((g) => g.key === skip || !filters[g.key]?.length || g.values(door).some((v) => filters[g.key].includes(v)));

export const groupOptions = (key, options) =>
  key === "material" ? MATERIAL_OPTIONS : key === "glas" ? GLASS_FILTER : options.filter((o) => o.group === key);

export const labelsOf = (ids, opts) => ids.map((i) => opts[i]?.label).filter(Boolean);
export const materialLabel = (m) => (m === "aluminium" ? "Aluminium" : "Kunststof");
export const doorOnderwerp = (d) => `${d.material}-deuren`;
export const srcSetOf = (path) => (/-720\.webp$/.test(path) ? `${path} 720w, ${path.replace(/-720\.webp$/, "-1280.webp")} 1280w` : undefined);

export const PRODUCT_KEY_LABELS = {
  kunststof_kozijnen: "Kunststof kozijnen", aluminium_kozijnen: "Aluminium kozijnen", kunststof_schuifpui: "Kunststof schuifpui",
  aluminium_schuifpui: "Aluminium schuifpui", aluminium_hefschuifpui: "Aluminium hefschuifpui", kunststof_deur: "Kunststof deuren",
  aluminium_deur: "Aluminium deuren",
};

const STYLE_LABELS = { vlak: "Vlakke profielen", verdiept: "Verdiepte profielen", beide: "Vlak en verdiept" };
export const systemSpecs = (s) => [
  ["Bouwdiepte", s.build_depth], ["Kameropbouw", s.chambers], ["Uf-waarde profiel", s.uf_value],
  ["Uw-waarde raam", s.uw_value && `${s.uw_value}${s.uw_config ? ` (${s.uw_config})` : ""}`], ["Glasdikte", s.glass_thickness],
  ["Dichting", s.sealing], ["Profielstijl", STYLE_LABELS[s.profile_style]], ["Kleuren", s.colors], ["Houtnerf", s.woodgrain],
  ["Toepassing", s.applications], ...(s.extra_specs || []).map((r) => [r.label, r.value]),
].filter(([, v]) => v);
